/**
 * Minimal structural mirror of Payload's `Block` type, with a multi-locale
 * escape hatch. Lets reklaim-app author block sidecar configs (`*.payload.ts`)
 * without taking a `payload` runtime dependency.
 *
 * At cutover into `reklaim-services/apps/marketing/payload/blocks/`, this file
 * is deleted and the moved sidecar imports rewrite via a small `sed`. Note
 * the sidecar files in this repo use the RELATIVE import `from './_types'`
 * (they live alongside `_types.ts` in `src/components/blocks/`), so the
 * cutover codemod runs over the moved files in their new home (e.g.
 * `apps/marketing/payload/blocks/`):
 *
 *   sed -i '' \
 *     -e "s|from './_types'|from 'payload'|" \
 *     -e "s|BlockConfig|Block|g" \
 *     apps/marketing/payload/blocks/*.ts
 *
 * (At cutover, sidecars are renamed from `<kebab>.payload.ts` to
 * `<kebab>.ts` during the `cp` step.)
 *
 * English-only authoring TODAY: a field's `label` may be a plain `string`.
 * Multi-locale FUTURE (when French copy lands): the same field accepts
 * `{ en, fr }` shape. Payload's localization machinery handles the runtime
 * swap; we just keep the type union here so blocks authored today don't have
 * to change.
 *
 * Reference: Section 3.3 of `.cursor/plans/phase-7-8-payload-prep.plan.md`.
 */

export type LocalizedString =
  | string
  | { en: string; fr?: string };

export type BlockFieldType =
  | 'text'
  | 'textarea'
  | 'richText'
  | 'number'
  | 'checkbox'
  | 'select'
  | 'array'
  | 'group'
  | 'upload'
  | 'relationship'
  | 'json'
  | 'radio'
  | 'date';

export interface BlockField {
  name: string;
  type: BlockFieldType;
  required?: boolean;
  unique?: boolean;
  defaultValue?: unknown;
  hasMany?: boolean;
  relationTo?: string;
  options?: Array<{ label: LocalizedString; value: string }>;
  fields?: BlockField[];
  minRows?: number;
  maxRows?: number;
  label?: LocalizedString;
  labels?: { singular: LocalizedString; plural: LocalizedString };
  admin?: {
    description?: LocalizedString;
    condition?: (data: unknown, sibling: unknown) => boolean;
    rows?: number;
    position?: 'sidebar';
  };
}

export interface BlockConfig {
  slug: string;
  interfaceName?: string;
  imageURL?: string;
  labels?: { singular: LocalizedString; plural: LocalizedString };
  fields: BlockField[];
}

export const linkField = (opts: {
  name?: string;
  label?: LocalizedString;
  required?: boolean;
  defaultValue?: string;
}): BlockField => ({
  name: opts.name ?? 'url',
  type: 'text',
  required: opts.required,
  defaultValue: opts.defaultValue,
  label: opts.label,
});

export const iconField = (opts: {
  name: string;
  label: LocalizedString;
  required?: boolean;
  defaultValue?: string;
}): BlockField => ({
  name: opts.name,
  type: 'text',
  required: opts.required,
  defaultValue: opts.defaultValue,
  label: opts.label,
});
